import type { Capabilities, DeviceClass } from "./wire.js";

interface PermissionsPolicyLike {
  allowsFeature(feature: string): boolean;
}

export interface CapabilityEnvironment {
  userAgent: string;
  hasGetDisplayMedia: boolean;
  permissionsPolicy?: PermissionsPolicyLike;
  maxTouchPoints?: number;
}

export function permissionsPolicyAllowsCapture(
  policy: PermissionsPolicyLike | undefined,
): boolean {
  if (policy === undefined) return true;
  try {
    return policy.allowsFeature("display-capture");
  } catch {
    return false;
  }
}

export function detectDeviceClass(
  userAgent: string,
  maxTouchPoints = 0,
): DeviceClass {
  const ua = userAgent.toLowerCase();
  if (ua.includes("web0s") || ua.includes("webos")) return "webos";
  if (ua.includes("cros")) return "chromebook";
  if (ua.includes("android")) return "android";
  if (
    /iphone|ipad|ipod/u.test(ua) ||
    (ua.includes("macintosh") && maxTouchPoints > 1)
  ) {
    return "ios";
  }
  if (/windows|macintosh|linux/u.test(ua)) return "desktop";
  return "unknown";
}

export function detectCapabilities(
  environment: CapabilityEnvironment,
): Capabilities {
  const deviceClass = detectDeviceClass(
    environment.userAgent,
    environment.maxTouchPoints,
  );
  return {
    canCaptureScreen:
      deviceClass !== "webos" &&
      deviceClass !== "ios" &&
      environment.hasGetDisplayMedia &&
      permissionsPolicyAllowsCapture(environment.permissionsPolicy),
    deviceClass,
    h264Only: deviceClass === "webos",
    userAgent: environment.userAgent,
  };
}

export function detectBrowserCapabilities(): Capabilities {
  const documentPolicy = (
    document as Document & { permissionsPolicy?: PermissionsPolicyLike }
  ).permissionsPolicy;
  return detectCapabilities({
    userAgent: navigator.userAgent,
    hasGetDisplayMedia:
      navigator.mediaDevices !== undefined &&
      typeof navigator.mediaDevices.getDisplayMedia === "function",
    permissionsPolicy: documentPolicy,
    maxTouchPoints: navigator.maxTouchPoints,
  });
}
